import type { GoalTypeOption, PlatformSyncResult } from "@repo/shared/types";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { AlertMessage } from "@/components/ui/alert-message";
import { FlowComponentHeader } from "@/components/ui/flow-component-header";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import * as goalsApi from "@/lib/api/flow-instances/goals";
import * as publicGoalsApi from "@/lib/api/public-flow/goals";
import { usePublicSession } from "@/lib/context/public-session-context";
import { useInvalidateFlowCompletion } from "@/lib/hooks/use-flow-instances/use-flow-completion";
import { goalSnapshotQueryKeys } from "@/lib/hooks/use-flow-instances/use-goal-snapshots";
import { goalQueryKeys } from "@/lib/hooks/use-flow-instances/use-goals";
import {
  goalPlatformQueryKeys,
  useGoalPlatformState,
  useSetGoalPlatform,
} from "@/lib/hooks/use-goal-platforms";
import { useUserLanguage } from "@/lib/i18n/context";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import type { ComponentStyleType } from "@/lib/types/flow";
import type { GoalListItem } from "@/lib/types/goal";
import { InfoNote } from "@/pages/clients/detail/flows/instance/info-note";
import { GoalDescriptionField } from "./goal-description-field";
import { T } from "./goal-input-widget.i18n";
import { GoalPlatformField } from "./goal-platform-field";
import { GoalTypePicker } from "./goal-type-picker";
import { useGoalPlatformCascade } from "./use-goal-platform-cascade";

// QP-150 — goal_input is goal-level only now (name/type/description). Schedule/amount/portfolio-type
// moved to portfolio_input (edited per portfolio on the portfolio page).
type GoalInputWidgetProps = {
  flowInstanceId: string;
  goalId: string;
  initialData?: {
    name: string;
    goalType?: string | null;
    description?: string | null;
  };
  disabled?: boolean;
  // Configurable styling props
  style?: ComponentStyleType;
  title?: string;
  description?: string;
  /**
   * QP-187 — the flow collection's goal types. Non-empty → a REQUIRED type picker
   * renders above the name field (tiles when any type has an icon, radios otherwise).
   * Empty/undefined → pre-QP-187 behavior (name only).
   */
  goalTypes?: GoalTypeOption[];
  /** QP-187 — component config toggle for the optional description textarea. */
  showDescription?: boolean;
  // Callback after a successful save
  onSaveComplete?: () => void | Promise<void>;
  /**
   * Single per-widget error flag (H2). When true, the widget renders an outer
   * `border-destructive` plus a single "Complete goal details" message-under-title.
   */
  hasValidationError?: boolean;
  /** QL-188 — group answering note shown under the header; null for non-group clients. */
  groupNote?: string | null;
};

type FormSnapshot = {
  goalId: string;
  name: string;
  goalType: string;
  description: string;
};

/**
 * Goal Input Widget (QP-150 / QP-187)
 * Inline form for editing goal-level details (type, name, description). Used on goal pages in
 * single- and multi-goal flows. Portfolio schedule/amount/type live in the Portfolio Input widget.
 */
export function GoalInputWidget({
  flowInstanceId,
  goalId,
  initialData,
  disabled = false,
  style = "border",
  title,
  description,
  goalTypes = [],
  showDescription = false,
  onSaveComplete,
  hasValidationError = false,
  groupNote = null,
}: GoalInputWidgetProps) {
  const { isPublic, sessionToken } = usePublicSession();
  const { userLanguage } = useUserLanguage();
  const localize = useLocalize();
  const t = useT(T);
  const queryClient = useQueryClient();
  const invalidateCompletion = useInvalidateFlowCompletion();

  // QL-36 — the server sends the types this client may pick, plus any type an
  // existing goal in the instance still holds (tagged `available: false`). Show
  // the latter only on the goal that owns it, so a config change never wipes a
  // client's stored answer but also never offers it to a goal that lacks it.
  const visibleGoalTypes = goalTypes.filter(
    (gt) => gt.available !== false || gt.id === initialData?.goalType,
  );

  // ── QP-195 — per-goal platform state (server-owned eligibility) ──
  const { platformState } = useGoalPlatformState(goalId);
  const setPlatform = useSetGoalPlatform(goalId);

  /** Shared post-cascade bookkeeping: warning toast + snapshot/goal/completion invalidation. */
  const handlePlatformCascade = useGoalPlatformCascade({ goalId, flowInstanceId });

  const handleSelectPlatform = (platformId: string) => {
    if (disabled || !platformId || platformId === platformState?.selectedId) return;
    setPlatform.mutate(
      { platformId, lang: userLanguage },
      {
        onSuccess: async (result) => {
          if (!result.success) {
            showAlert(t.error, result.error || t.failedToSetPlatform, "destructive");
            return;
          }
          handlePlatformCascade(result);
          if (onSaveComplete) await onSaveComplete();
        },
        onError: (err: Error) =>
          showAlert(t.error, err.message || t.failedToSetPlatform, "destructive"),
      },
    );
  };

  // Self-healing auto-apply: when the evaluation is resolved and exactly ONE
  // platform is eligible but the goal has none saved yet (e.g. the server-side
  // cascade has not been triggered since the platforms were configured), apply
  // it via the selection endpoint so the pill state matches the stored goal.
  const autoAppliedRef = useRef<string | null>(null);
  useEffect(() => {
    if (disabled || !platformState?.hasPlatforms || !platformState.resolved) return;
    if (platformState.eligible.length !== 1) return;
    const id = platformState.eligible[0]!.id;
    if (platformState.selectedId === id) return;
    if (platformState.selectedId) return; // never silently switch a saved platform
    if (autoAppliedRef.current === `${goalId}:${id}`) return;
    autoAppliedRef.current = `${goalId}:${id}`;
    handleSelectPlatform(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platformState, goalId, disabled]);

  // Local form state — initialized from initialData
  const [name, setName] = useState(initialData?.name ?? "");
  const [goalType, setGoalType] = useState(initialData?.goalType ?? "");
  const [goalDescription, setGoalDescription] = useState(initialData?.description ?? "");

  // Track previous values to detect actual user changes (not initial mount or goal switching).
  const prevValuesRef = useRef<FormSnapshot | null>(null);

  const snapshot = (): FormSnapshot => ({ goalId, name, goalType, description: goalDescription });

  // Reset form state when goalId changes (switching between goals).
  useEffect(() => {
    const newName = initialData?.name ?? "";
    const newGoalType = initialData?.goalType ?? "";
    const newDescription = initialData?.description ?? "";
    setName(newName);
    setGoalType(newGoalType);
    setGoalDescription(newDescription);
    prevValuesRef.current = {
      goalId,
      name: newName,
      goalType: newGoalType,
      description: newDescription,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goalId]);

  // Adopt EXTERNAL updates to this goal (e.g. an AI tool renames it) when initialData changes
  // for the SAME goalId and the form has no unsaved local edit on that field.
  useEffect(() => {
    const baseline = prevValuesRef.current;
    if (!baseline || baseline.goalId !== goalId) return;
    const incoming = {
      name: initialData?.name ?? "",
      goalType: initialData?.goalType ?? "",
      description: initialData?.description ?? "",
    };
    const serverChanged =
      baseline.name !== incoming.name ||
      baseline.goalType !== incoming.goalType ||
      baseline.description !== incoming.description;
    if (!serverChanged) return; // server unchanged
    const hasLocalEdits =
      name !== baseline.name ||
      goalType !== baseline.goalType ||
      goalDescription !== baseline.description;
    if (hasLocalEdits) return; // user has pending edits — leave them
    setName(incoming.name);
    setGoalType(incoming.goalType);
    setGoalDescription(incoming.description);
    prevValuesRef.current = { goalId, ...incoming };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

  // Local state for UI only
  const [isSaving, setIsSaving] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertConfig, setAlertConfig] = useState({
    title: "",
    description: "",
    variant: "default" as "default" | "destructive",
  });

  const showAlert = (
    alertTitle: string,
    alertDescription: string,
    variant: "default" | "destructive" = "default",
  ) => {
    setAlertConfig({ title: alertTitle, description: alertDescription, variant });
    setAlertOpen(true);
  };

  const handleSave = async () => {
    if (disabled) return;
    setIsSaving(true);
    try {
      const formData: Record<string, unknown> = { name };
      // Only send goalType when the user has one (avoid clobbering with "").
      if (goalType) formData.goalType = goalType;
      // Only send description when the textarea is enabled for this component.
      if (showDescription) formData.description = goalDescription;

      const saved = isPublic
        ? await publicGoalsApi.updatePublicGoal(goalId, formData, sessionToken)
        : await goalsApi.updateGoal(goalId, formData);

      // QP-195 — a goalType change may have re-synced the goal's platform
      // server-side (auto-apply/clear + cascade). Surface the removed-products
      // warning and refresh the affected queries.
      const platformSync = (saved as { platformSync?: PlatformSyncResult }).platformSync;
      if (platformSync?.changed) {
        handlePlatformCascade(platformSync);
      } else {
        // Eligibility depends on the goal type — refetch the platform state.
        queryClient.invalidateQueries({ queryKey: goalPlatformQueryKeys.state(goalId) });
      }

      // Update goals cache in-place — avoids a refetch just for a goal-level edit.
      queryClient.setQueryData(
        goalQueryKeys.goals(flowInstanceId),
        (old: GoalListItem[] | undefined) =>
          old?.map((g) =>
            g.id === goalId
              ? {
                  ...g,
                  name,
                  ...(goalType ? { goalType } : {}),
                  ...(showDescription ? { description: goalDescription || null } : {}),
                }
              : g,
          ),
      );

      // This widget calls goalsApi.updateGoal directly (bypassing useUpdateGoal), so invalidate
      // completion here so the progress bar refreshes after an edit (advisor path only).
      if (!isPublic) {
        invalidateCompletion(flowInstanceId);
        // QL-375 — a goal-type change re-resolves the goal's mandate server-side
        // (the three-way, and the platform it may have cleared): the mandate
        // panel and the custom portfolio's empty-state button read it.
        queryClient.invalidateQueries({ queryKey: goalSnapshotQueryKeys.mandate(goalId) });
      }

      if (onSaveComplete) {
        await onSaveComplete();
      }
    } catch (_err) {
      showAlert(t.error, t.failedToSave, "destructive");
    } finally {
      setIsSaving(false);
    }
  };

  // Auto-save on change (debounced).
  useEffect(() => {
    if (!prevValuesRef.current || prevValuesRef.current.goalId !== goalId) {
      prevValuesRef.current = snapshot();
      return;
    }
    const prev = prevValuesRef.current;
    if (prev.name === name && prev.goalType === goalType && prev.description === goalDescription) {
      return;
    }
    prevValuesRef.current = snapshot();
    const timeoutId = setTimeout(() => {
      handleSave();
    }, 1000);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goalId, name, goalType, goalDescription]);

  // QP-187 — selecting a type sets goalType and replaces the name with the
  // type's label (resolved to the current language).
  const handleSelectType = (goalTypeId: string) => {
    if (disabled) return;
    setGoalType(goalTypeId);
    const selected = goalTypes.find((gt) => gt.id === goalTypeId);
    if (selected) setName(localize(selected.labelI18n));
  };

  // `relative` hosts the saving indicator, which is positioned out of the flow
  // so the form never jumps a line down and back on every autosave. The padded
  // variants have a corner gutter for it; the unstyled one reserves a top slot
  // (`pt-5`) so the badge never paints over the first line of content.
  const getContainerClass = () => {
    if (hasValidationError) {
      return "relative border border-destructive rounded-lg p-6 space-y-6";
    }
    switch (style) {
      case "border":
        return "relative border rounded-lg p-6 space-y-6";
      case "box":
        return "relative rounded-lg p-6 space-y-6 bg-muted/50";
      default:
        return "relative pt-5 space-y-6";
    }
  };
  const isPadded = hasValidationError || style === "border" || style === "box";

  return (
    <>
      <div className={getContainerClass()}>
        <FlowComponentHeader title={title} description={description} />
        {groupNote && <InfoNote text={groupNote} />}

        {hasValidationError && <p className="text-sm text-destructive">{t.incompleteMessage}</p>}

        {isSaving && (
          <div
            aria-live="polite"
            className={`absolute text-xs text-muted-foreground pointer-events-none ${isPadded ? "top-2 right-3" : "top-0 right-0"}`}
          >
            {t.saving}
          </div>
        )}

        {/* QP-187 — required goal-type picker (only when the collection has goal types) */}
        <GoalTypePicker
          idPrefix={`goal-${goalId}`}
          goalTypes={visibleGoalTypes}
          value={goalType}
          onValueChange={handleSelectType}
          disabled={disabled}
        />

        {/* QP-195 — platform (only when the flow's collection has platforms); a pick applies immediately */}
        <GoalPlatformField
          platformState={platformState}
          value={platformState?.selectedId ?? ""}
          onValueChange={handleSelectPlatform}
          disabled={disabled || setPlatform.isPending}
        />

        <div className="space-y-2">
          <Label htmlFor="goalName">
            {t.goalName} <span className="text-destructive">*</span>
          </Label>
          <Input
            id="goalName"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t.goalNamePlaceholder}
            disabled={disabled}
            required
          />
        </div>

        {/* QP-187 — optional description with live character counter */}
        {showDescription && (
          <GoalDescriptionField
            id="goalDescription"
            value={goalDescription}
            onChange={setGoalDescription}
            disabled={disabled}
          />
        )}
      </div>

      <AlertMessage
        open={alertOpen}
        onOpenChange={setAlertOpen}
        title={alertConfig.title}
        description={alertConfig.description}
        variant={alertConfig.variant}
      />
    </>
  );
}
